/* For Schools: /schools
   ----------------------------------------------------------------------
   Kidsology's offer to schools in five parts, from the build prompt
   "KDX_WEB_Schools_Page_Build_Prompt" v1.1 (5 October 2026). Copy lives
   in src/data/schools.js; the layout reuses the catalogue structure of
   Camps & Trips (src/components/catalog), as CLAUDE.md asks.

   Order: hero → five ways (overview cards) → Future Skills Programmes
   → Future Pathways Showcase (with Give and Learn) → Foundation
   Programmes → Career Pathways & Internships → Industry Workshops &
   Guest Speakers → Trips & Camps (with the UK 2026 results) → why
   schools choose Kidsology → how it works → FAQ → enquiry form.

   The enquiry writes to `partnershipLeads` exactly like the home page
   "For Partners" form, with partnerType "school" and the chosen
   interests leading the message (PartnerEnquiryForm). */

import { SIZES } from "../lib/img";
import React from "react";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import LocaleLink from "../components/LocaleLink";
import { useLocale, useL } from "../i18n/LocaleContext";
import { SCHOOLS } from "../data/schools";
import { CAMP_BY_SLUG } from "../data/camps";
import { faqPageSchema, breadcrumbSchema } from "../data/schemas";
import { isDraftPath } from "../seo/site.mjs";
import {
  Pic, CatalogCard, CheckList, ThreeCards, FloatingCta, useHashScroll,
  Accent, SectionHead, StepCards, InfoCard, DataTable, FaqList, PartnerEnquiryForm, ContactList,
} from "../components/catalog/CatalogParts";
import { ResultsBand } from "./trips/TripBlocks";
import { track } from "../lib/analytics";

/* The refund policy is linked once its page is published (it is
   prepared as a draft route until the founder confirms the open
   clauses; see src/seo/site.mjs DRAFT_ROUTES). */
const REFUND_PATH = "/refund-policy";

export default function Schools() {
  const { locale } = useLocale();
  const c = useL(SCHOOLS);
  const uk = useL(CAMP_BY_SLUG["uk-summer"]);
  useHashScroll();
  const refundLive = !isDraftPath(REFUND_PATH);

  return (
    <div className="cat-page">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        path="/schools"
        image={`${SCHOOLS.hero.photos[0].src}.jpg`}
        schema={[
          faqPageSchema(SCHOOLS.faq, locale),
          breadcrumbSchema([
            { name: SCHOOLS.home, path: "/" },
            { name: SCHOOLS.hero.eyebrow, path: "/schools" },
          ], locale),
        ]}
      />
      <SiteHeader ctaHref="#enquire" />
      <main id="main">
        {/* ── Hero ── */}
        <header className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-hero-copy">
              <p className="cat-eyebrow">{c.hero.eyebrow}</p>
              <h1 className="cat-title"><Accent title={c.hero.title} /></h1>
              <p className="cat-lead">{c.hero.lead}</p>
              <ul className="cat-stats cat-stats--words">
                {c.hero.stats.map((s, i) => (
                  <li key={i}>
                    <span className="cat-stat-v">{s.v}</span>
                    <span className="cat-stat-l">{s.l}</span>
                  </li>
                ))}
              </ul>
              <div className="cat-actions cat-hero-actions">
                <a href="#offer" className="btn btn-gold-lg" onClick={() => track("schools_hero_cta_click", { target: "offer" })}>{c.hero.ctaOffer}</a>
                <a href="#enquire" className="btn btn-outline-white" onClick={() => track("schools_hero_cta_click", { target: "enquire" })}>{c.hero.ctaTalk}</a>
              </div>
            </div>
            <div className="cat-mosaic">
              {c.hero.photos.map((p, i) => <Pic key={p.src} src={p.src} alt={p.alt} eager={i < 2} sizes={SIZES.strip} />)}
            </div>
          </div>
        </header>

        {/* ── 1. Five ways to work with Kidsology ── */}
        <section className="cat-section" id="offer" aria-labelledby="offer-title">
          <div className="cat-wrap">
            <SectionHead id="offer-title" title={c.overview.title} lead={c.overview.lead} />
            <ul className="cat-grid cat-grid--four">
              {c.overview.cards.map((card) => (
                <li key={card.href}>
                  <CatalogCard href={card.href} photo={card.photo} eyebrow={card.eyebrow} title={card.title} text={card.text} more={c.overview.more} />
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── 2. Future Skills Programmes ── */}
        <section className="cat-section cat-section--white" id="future-skills" aria-labelledby="fs-title">
          <div className="cat-wrap">
            <SectionHead id="fs-title" eyebrow={c.futureSkills.eyebrow} title={c.futureSkills.title} lead={c.futureSkills.lead} />
            <StepCards items={c.futureSkills.steps} goal />
            <ul className="cat-cards">
              {c.futureSkills.programmes.map((p, i) => (
                <li key={i}><InfoCard title={p.title} pill={p.pill} tone={p.tone} text={p.text} bullets={p.bullets} /></li>
              ))}
            </ul>
            <div className="cat-panel cat-panel--accent">
              <h3 className="cat-panel-title">{c.futureSkills.includesTitle}</h3>
              <CheckList items={c.futureSkills.includes} columns />
            </div>
            <div>
              <DataTable caption={c.futureSkills.deliveryTitle} head={c.futureSkills.deliveryHead} rows={c.futureSkills.delivery} />
              {c.futureSkills.notes.map((n, i) => <p key={i} className="cat-line cat-line--after">{n}</p>)}
            </div>
          </div>
        </section>

        {/* ── 3. The Future Pathways Showcase ── */}
        <section className="cat-section cat-band" id="showcase" aria-labelledby="sc-title">
          <div className="cat-wrap">
            <SectionHead id="sc-title" eyebrow={c.showcase.eyebrow} title={c.showcase.title} lead={c.showcase.lead} />
            <StepCards items={c.showcase.steps} dark />
            <div>
              <h3 className="cat-subhead">{c.showcase.outcomesTitle}</h3>
              <ul className="cat-outcomes">
                {c.showcase.outcomes.map((o, i) => (
                  <li key={i}>
                    <h4>{o.t}</h4>
                    {o.b && <p>{o.b}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        <section className="cat-section" aria-label={c.showcase.eyebrow}>
          <div className="cat-wrap cat-split">
            <div className="cat-panel cat-panel--accent">
              <h3 className="cat-panel-title">{c.showcase.factsTitle}</h3>
              <CheckList items={c.showcase.facts} />
              <p className="cat-small">{c.showcase.smallPrint}</p>
            </div>
            <div className="cat-panel cat-panel--green" id="give-and-learn">
              <h3 className="cat-panel-title">{c.showcase.giveLearn.title}</h3>
              <p className="cat-bigfig">{c.showcase.giveLearn.figure}</p>
              <p className="cat-text">{c.showcase.giveLearn.text}</p>
              <div className="cat-btn-row">
                <a href="#enquire" className="btn btn-gold-lg" onClick={() => track("schools_give_learn_click")}>{c.showcase.giveLearn.cta}</a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. Foundation Programmes ── */}
        <section className="cat-section cat-section--white" id="foundation" aria-labelledby="fd-title">
          <div className="cat-wrap">
            <SectionHead id="fd-title" eyebrow={c.foundation.eyebrow} title={c.foundation.title} />
            <ul className="cat-cards cat-cards--two">
              {c.foundation.programmes.map((p, i) => (
                <li key={i}><InfoCard title={p.title} pill={p.pill} tone={p.tone} text={p.text} bullets={p.bullets} /></li>
              ))}
            </ul>
            <p className="cat-line">{c.foundation.note}</p>
          </div>
        </section>

        {/* ── 5. Career Pathways & Internships ── */}
        <section className="cat-section" id="pathways" aria-labelledby="pw-title">
          <div className="cat-wrap">
            <SectionHead id="pw-title" eyebrow={c.pathways.eyebrow} title={c.pathways.title} lead={c.pathways.lead} />
            <ul className="cat-cards cat-cards--two">
              {c.pathways.cards.map((p, i) => (
                <li key={i}><InfoCard title={p.title} pill={p.pill} tone={p.tone} text={p.text} link={p.link} /></li>
              ))}
            </ul>
            <p className="cat-line">{c.pathways.note}</p>
          </div>
        </section>

        {/* ── 6. Industry Workshops & Guest Speakers ── */}
        <section className="cat-section cat-section--white" id="workshops" aria-labelledby="ws-title">
          <div className="cat-wrap">
            <SectionHead id="ws-title" eyebrow={c.workshops.eyebrow} title={c.workshops.title} lead={c.workshops.lead} />
            <p className="cat-pill-row"><span className="cat-pill cat-pill--green">{c.workshops.pill}</span></p>
            <ThreeCards items={c.workshops.cards} />
            <p className="cat-line">{c.workshops.note}</p>
          </div>
        </section>

        {/* ── 7. Trips & Camps ── */}
        <section className="cat-section" id="trips" aria-labelledby="tc-title">
          <div className="cat-wrap">
            <SectionHead id="tc-title" eyebrow={c.trips.eyebrow} title={c.trips.title} />
            <ul className="cat-cards cat-cards--two">
              {c.trips.cards.map((p, i) => (
                <li key={i}><InfoCard title={p.title} photo={p.photo} text={p.text} link={{ href: p.href, label: p.more }} /></li>
              ))}
            </ul>
            <p className="cat-line">{c.trips.note}</p>
          </div>
        </section>
        <ResultsBand
          results={uk.results}
          eyebrow={c.trips.resultsEyebrow}
          title={c.trips.resultsTitle}
          link={<LocaleLink to={uk.pastHref}>{uk.pastLabel}</LocaleLink>}
        />

        {/* ── Why schools choose Kidsology ── */}
        <section className="cat-section cat-section--white" aria-labelledby="why-title">
          <div className="cat-wrap">
            <SectionHead id="why-title" title={c.why.title} />
            <div className="cat-two">
              {c.why.columns.map((col, i) => (
                <div key={i} className="cat-panel cat-panel--accent">
                  <h3 className="cat-panel-title">{col.title}</h3>
                  <CheckList items={col.items} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="cat-section" aria-labelledby="how-title">
          <div className="cat-wrap">
            <h2 id="how-title" className="cat-h2 cat-h2--gap">{c.how.title}</h2>
            <ThreeCards items={c.how.steps} numbered />
            <div className="cat-lists">
              {c.how.lists.map((l, i) => (
                <div key={i} className="cat-panel">
                  <h3 className="cat-panel-title">{l.title}</h3>
                  <CheckList items={l.items} />
                  {i === 0 && refundLive && (
                    <p className="cat-small"><LocaleLink to={REFUND_PATH} className="cat-link">{c.how.refundLink}</LocaleLink></p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="cat-section cat-section--white" aria-labelledby="faq-title">
          <div className="cat-wrap cat-wrap--narrow">
            <h2 id="faq-title" className="cat-h2 cat-h2--gap">{c.faqTitle}</h2>
            <FaqList items={c.faq} />
          </div>
        </section>

        {/* ── Enquiry ── */}
        <section className="cat-section cat-section--navy" id="enquire" aria-labelledby="enquire-title">
          <div className="cat-enquiry-inner">
            <div>
              <p className="cat-eyebrow">{c.enquiry.eyebrow}</p>
              <h2 id="enquire-title" className="cat-h2"><Accent title={c.enquiry.title} /></h2>
              <p className="cat-sub">{c.enquiry.lead}</p>
              <ContactList contacts={c.enquiry.contacts} />
            </div>
            <div className="cat-form">
              <PartnerEnquiryForm copy={c.enquiry.form} options={c.enquiry.interestOptions} partnerType="school" event="schools_enquiry_submit" idPrefix="se" />
            </div>
          </div>
        </section>
      </main>
      <FloatingCta
        href="#enquire"
        label={c.floatCta}
        heroSelector=".cat-hero-actions"
        targetId="enquire"
        event="schools_float_cta_click"
      />
    </div>
  );
}
